import {
  AfterViewInit, Component, ElementRef, Input,
  NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild
} from '@angular/core';

/**
 * Estados del orbe. Hoy solo se usan 'idle' y 'listening', pero el resto ya
 * está definido para cuando el asistente de voz corra dentro de Aliwell.
 */
export type AiOrbMode = 'idle' | 'listening' | 'speaking' | 'thinking';

interface AiOrbPalette {
  /** Los 4 topes del degradado que rellena el orbe. */
  s: number[][];
  /** Color del halo. */
  g: number[];
  /** Opacidad base del halo. */
  ga: number;
}

/** Paletas por estado. Los tonos salen de la guía de estilo de Aliwell. */
const PAL: { [mode: string]: AiOrbPalette } = {
  idle:      { s: [[1, 200, 189], [120, 215, 240], [168, 170, 255], [1, 200, 189]], g: [1, 200, 189],   ga: 0.30 },
  listening: { s: [[7, 109, 242], [1, 200, 189], [73, 243, 255], [7, 109, 242]],    g: [1, 200, 189],   ga: 0.34 },
  speaking:  { s: [[118, 20, 217], [142, 146, 255], [226, 79, 247], [118, 20, 217]], g: [142, 146, 255], ga: 0.40 },
  thinking:  { s: [[13, 86, 94], [1, 200, 189], [142, 146, 255], [13, 86, 94]],     g: [1, 200, 189],   ga: 0.30 }
};

/**
 * Los mismos colores de arriba, pero repartidos como luz, medio, sombra y
 * rebote en lugar de como cuatro topes de un degradado en diagonal.
 *
 * Ese degradado en diagonal era el problema: cuatro colores muy separados
 * cruzando la figura en línea recta dejan bandas visibles, y las bandas son lo
 * que hace que un orbe se vea como un efecto de plantilla. Un cuerpo real no
 * tiene rayas: tiene una luz y una sombra.
 *
 * El orden es [luz, medio, sombra, rebote]. El rebote es la luz que devuelve
 * el entorno por el lado en sombra; sin él la esfera se ve plana de ese lado.
 */
const SPH: { [mode: string]: number[][] } = {
  idle:      [[212, 250, 246], [ 90, 214, 215], [ 96, 120, 222], [168, 170, 255]],
  listening: [[206, 252, 250], [ 34, 200, 206], [ 16,  92, 196], [ 73, 243, 255]],
  speaking:  [[240, 215, 255], [176, 140, 250], [ 96,  18, 180], [226,  79, 247]],
  thinking:  [[200, 246, 240], [ 40, 190, 185], [ 13,  80,  90], [142, 146, 255]]
};

/**
 * Ruido finísimo, generado una sola vez y compartido por todos los orbes.
 *
 * Cualquier degradado suave muestra escalones de color en pantallas de 8 bits.
 * Un grano casi invisible por encima los rompe: no se ve, se nota, y es la
 * diferencia entre un degradado de producto y uno de plantilla.
 */
let NOISE: HTMLCanvasElement = null;
function noiseTile(): HTMLCanvasElement {
  if (NOISE) { return NOISE; }
  const n = 128;
  const c = document.createElement('canvas');
  c.width = n; c.height = n;
  const x = c.getContext('2d');
  const img = x.createImageData(n, n);
  const px = img.data;
  for (let i = 0; i < n * n; i++) {
    const v = (Math.random() - 0.5) * 255;
    px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = v > 0 ? 255 : 0;
    px[i * 4 + 3] = Math.abs(v) * 0.5;
  }
  x.putImageData(img, 0, 0);
  NOISE = c;
  return c;
}

/**
 * Capas del orbe. Cada una deforma su contorno con tres senos de distinta
 * frecuencia (k) y velocidad (sp): al superponerse nunca repiten la misma
 * silueta, y de ahí sale la sensación de que el orbe está vivo.
 */
const LAYERS = [
  { k: [3, 5, 7],   sp: [0.62, -0.41, 0.29],  amp: 1.00, sc: 1.00 },
  { k: [4, 6, 9],   sp: [-0.47, 0.56, -0.34], amp: 0.80, sc: 0.93 },
  { k: [5, 8, 11],  sp: [0.36, -0.31, 0.49],  amp: 0.64, sc: 0.86 }
];

/** Cuántas partículas orbitan alrededor del orbe. */
const PARTICLES = 26;

/**
 * Orbe animado del asistente de IA: un blob de color que respira, con halo y
 * partículas en órbita, dibujado a mano en un canvas (sin librerías).
 *
 * El tamaño lo decide quien lo usa —el canvas se adapta a la caja del padre—,
 * así que basta con darle ancho y alto por CSS.
 */
@Component({
  selector: 'app-ai-orb',
  template: '<canvas #canvas aria-hidden="true"></canvas>',
  styles: [`
    :host { display: block; line-height: 0; }
    canvas { display: block; width: 100%; height: 100%; }
  `]
})
export class AiOrbComponent implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('canvas') private canvasRef: ElementRef<HTMLCanvasElement>;

  /** Estado que pinta el orbe: cambia la paleta con una transición suave. */
  @Input() public mode: AiOrbMode = 'idle';

  /** Qué tan "encendido" está, de 0 a 1. En reposo el orbe se mueve solo. */
  @Input() public level: number = 0;

  private ctx: CanvasRenderingContext2D;
  private W: number = 0;
  private H: number = 0;
  /** Se topa en 2: más resolución que eso no se nota y sí cuesta pintarla. */
  private readonly DPR: number = Math.min(window.devicePixelRatio || 1, 2);

  private resizeObserver: ResizeObserver;
  private frameId: number;

  /** Reloj de la animación. */
  private t: number = 0;
  /** Nivel al que tiende (el @Input) y nivel actual, que lo persigue suave. */
  private target: number = 0;
  private curLevel: number = 0;

  /** Colores actuales: se acercan poco a poco a los de la paleta del modo. */
  private cur: number[][] = PAL.idle.s.map(c => c.slice());
  private curG: number[] = PAL.idle.g.slice();
  private curGA: number = PAL.idle.ga;

  /** Luz, medio, sombra y rebote actuales. Persiguen al modo igual que el resto. */
  private curS: number[][] = SPH.idle.map(c => c.slice());

  /** Partículas en órbita: ángulo, radio, velocidad, tamaño y fase. */
  private particles: { a: number, r: number, v: number, s: number, ph: number }[] = [];

  constructor(private zone: NgZone) {
    for (let i = 0; i < PARTICLES; i++) {
      this.particles.push({
        a: Math.random() * 6.28,
        r: 1.06 + Math.random() * 0.5,
        v: 0.10 + Math.random() * 0.26,
        s: 0.8 + Math.random() * 1.4,
        ph: Math.random() * 6.28
      });
    }
  }

  public ngAfterViewInit(): void {
    this.ctx = this.canvasRef.nativeElement.getContext('2d');
    this.applyInputs();

    // Fuera de la zona de Angular: si no, cada cuadro dispararía una detección
    // de cambios y la pantalla completa se revisaría 60 veces por segundo.
    this.zone.runOutsideAngular(() => {
      this.resize();
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvasRef.nativeElement);
      this.frameId = requestAnimationFrame(this.frame);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.applyInputs();
  }

  public ngOnDestroy(): void {
    // Sin esto el bucle seguiría corriendo aunque la tarjeta ya no exista.
    if (this.frameId) { cancelAnimationFrame(this.frameId); }
    if (this.resizeObserver) { this.resizeObserver.disconnect(); }
  }

  private applyInputs(): void {
    this.target = Math.max(0, Math.min(1, this.level || 0));
  }

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    const r = canvas.getBoundingClientRect();
    if (!r.width) { return; }
    this.W = r.width;
    this.H = r.height;
    canvas.width = this.W * this.DPR;
    canvas.height = this.H * this.DPR;
    this.ctx.setTransform(this.DPR, 0, 0, this.DPR, 0, 0);
  }

  private rgb(c: number[]): string {
    return 'rgb(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ')';
  }

  private rgba(c: number[], a: number): string {
    return 'rgba(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ',' + a + ')';
  }

  /** Acerca un color al destino una fracción: de ahí sale el cambio suave. */
  private toward(c: number[], d: number[], f: number): void {
    for (let i = 0; i < 3; i++) { c[i] += (d[i] - c[i]) * f; }
  }

  /** Dibuja el contorno deformado de una capa. */
  private path(cx: number, cy: number, base: number, L: any, lvl: number): void {
    const N = 160;
    const ctx = this.ctx;
    ctx.beginPath();
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI * 2;
      let d = 0;
      for (let j = 0; j < 3; j++) { d += Math.sin(a * L.k[j] + this.t * L.sp[j]) * (1 / (j + 1.3)); }
      const breathe = Math.sin(this.t * 0.28) * 0.032 + Math.sin(this.t * 0.16 + 1.4) * 0.018;
      const morph = d * 0.038 * L.amp * (0.55 - lvl * 0.18);
      const r = base * L.sc * (1 + breathe + morph);
      const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.closePath();
  }

  /** Mezcla dos colores: sirve para inventar los pasos intermedios del cuerpo. */
  private mix(u: number[], v: number[], k: number): number[] {
    return [u[0] + (v[0] - u[0]) * k, u[1] + (v[1] - u[1]) * k, u[2] + (v[2] - u[2]) * k];
  }

  /**
   * El cuerpo del orbe. Cuatro cosas, en este orden:
   *
   * 1. Tres pasadas cada vez más grandes y tenues por debajo, para que el filo
   *    deje de ser una línea y se vuelva un paso.
   * 2. La luz esférica: el color ya no cruza en diagonal, se organiza alrededor
   *    de un punto de luz. Claro donde pega, oscuro del lado contrario.
   * 3. El rebote del entorno en el borde en sombra, y las dos capas internas
   *    reducidas a una insinuación —al 96/40/22 % de antes se veía su filo.
   * 4. Sombra interna en el contorno y un grano al 5 %, recortados a la figura.
   */
  private body(cx: number, cy: number, base: number, level: number): void {
    const ctx = this.ctx;
    const lit = this.curS[0], mid = this.curS[1], shade = this.curS[2], rim = this.curS[3];

    // 1 · Borde suave
    for (let i = 3; i >= 1; i--) {
      ctx.globalAlpha = 0.10 / i;
      ctx.fillStyle = this.rgb(this.mix(mid, lit, 0.3));
      this.path(cx, cy, base * (1 + i * 0.022), LAYERS[0], level);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // 2 · Luz esférica. Se mueve muy poco: lo justo para que no parezca fija.
    const la = this.t * 0.16;
    const lx = cx - base * 0.34 + Math.cos(la) * base * 0.10;
    const ly = cy - base * 0.36 + Math.sin(la) * base * 0.08;
    const g = ctx.createRadialGradient(lx, ly, base * 0.05, cx, cy, base * 1.32);
    g.addColorStop(0, this.rgb(lit));
    g.addColorStop(0.28, this.rgb(this.mix(lit, mid, 0.72)));
    g.addColorStop(0.52, this.rgb(mid));
    g.addColorStop(0.78, this.rgb(this.mix(mid, shade, 0.78)));
    g.addColorStop(1, this.rgb(shade));
    ctx.fillStyle = g;
    this.path(cx, cy, base, LAYERS[0], level);
    ctx.fill();

    // 3 · Rebote y capas internas
    const bx = cx + base * 0.42, by = cy + base * 0.46;
    const b = ctx.createRadialGradient(bx, by, 1, bx, by, base * 0.95);
    b.addColorStop(0, this.rgba(rim, 0.30 + level * 0.16));
    b.addColorStop(1, this.rgba(rim, 0));
    ctx.fillStyle = b;
    this.path(cx, cy, base, LAYERS[0], level);
    ctx.fill();

    ctx.globalAlpha = 0.10;
    ctx.fillStyle = this.rgb(mid);
    this.path(cx, cy, base, LAYERS[1], level);
    ctx.fill();
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = this.rgb(lit);
    this.path(cx, cy, base, LAYERS[2], level);
    ctx.fill();
    ctx.globalAlpha = 1;

    // 4 · Sombra interna y grano, recortados al contorno
    ctx.save();
    this.path(cx, cy, base, LAYERS[0], level);
    ctx.clip();

    const inner = ctx.createRadialGradient(cx, cy, base * 0.62, cx, cy, base * 1.02);
    inner.addColorStop(0, this.rgba(shade, 0));
    inner.addColorStop(1, this.rgba(shade, 0.38));
    ctx.fillStyle = inner;
    ctx.fillRect(cx - base * 1.3, cy - base * 1.3, base * 2.6, base * 2.6);

    ctx.globalAlpha = 0.05;
    const s = base * 2.4;
    ctx.drawImage(noiseTile(), cx - s / 2, cy - s / 2, s, s);
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /** Un cuadro de la animación. Propiedad-flecha para no perder el `this`. */
  private frame = (): void => {
    const ctx = this.ctx;
    this.t += 0.010;

    // En reposo el nivel nunca baja del todo: el orbe siempre late un poco.
    const piso = this.mode === 'idle' ? 0.10 + (Math.sin(this.t * 0.32) * 0.5 + 0.5) * 0.08 : 0.06;
    const tg = Math.max(this.target, piso);
    this.curLevel += (tg - this.curLevel) * 0.048;

    const P = PAL[this.mode] || PAL.idle;
    const S = SPH[this.mode] || SPH.idle;
    for (let i = 0; i < 4; i++) { this.toward(this.cur[i], P.s[i], 0.055); }
    for (let i = 0; i < 4; i++) { this.toward(this.curS[i], S[i], 0.055); }
    this.toward(this.curG, P.g, 0.055);
    this.curGA += (P.ga - this.curGA) * 0.055;

    if (!this.W) { this.frameId = requestAnimationFrame(this.frame); return; }

    ctx.clearRect(0, 0, this.W, this.H);
    const cx = this.W / 2, cy = this.H / 2;
    const level = this.curLevel;
    const base = Math.min(this.W, this.H) * (0.236 + level * 0.17);
    const glowR = Math.min(base * (2.05 + level * 0.45), Math.min(this.W, this.H) * 0.49);

    // Halo
    const ga = this.curGA * (0.7 + level * 0.7);
    const gl = ctx.createRadialGradient(cx, cy, Math.min(base * 0.5, glowR * 0.28), cx, cy, glowR);
    gl.addColorStop(0, this.rgba(this.curG, ga));
    gl.addColorStop(0.55, this.rgba(this.curG, ga * 0.3));
    gl.addColorStop(1, this.rgba(this.curG, 0));
    ctx.fillStyle = gl;
    ctx.fillRect(0, 0, this.W, this.H);

    // Partículas en órbita
    for (const p of this.particles) {
      const ang = p.a + this.t * p.v;
      const rr = base * p.r * (1 + level * 0.18) + Math.sin(this.t * 0.35 + p.ph) * base * 0.03;
      const x = cx + Math.cos(ang) * rr, y = cy + Math.sin(ang) * rr;
      ctx.globalAlpha = 0.16 + level * 0.42 + Math.sin(this.t * 1.6 + p.ph) * 0.07;
      ctx.fillStyle = this.rgb(this.cur[2]);
      ctx.beginPath();
      ctx.arc(x, y, p.s * (1 + level * 0.7), 0, 6.29);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Cuerpo
    this.body(cx, cy, base, level);

    // Brillo especular: el punto de luz que gira sobre la superficie.
    const sa = this.t * 0.34;
    const hg = ctx.createRadialGradient(
      cx + Math.cos(sa) * base * 0.34, cy + Math.sin(sa) * base * 0.36, 1, cx, cy, base * 1.1
    );
    hg.addColorStop(0, 'rgba(255,255,255,.48)');
    hg.addColorStop(0.45, 'rgba(255,255,255,.08)');
    hg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hg;
    this.path(cx, cy, base, LAYERS[0], level);
    ctx.fill();

    // Ondas: solo aparecen cuando el orbe tiene algo de nivel.
    if (level > 0.015) {
      ctx.globalAlpha = Math.min(0.55, level * 0.95);
      ctx.strokeStyle = this.rgb(this.cur[2]);
      ctx.lineWidth = 1.1;
      this.path(cx, cy, base * (1.2 + level * 0.3), LAYERS[1], level * 0.75);
      ctx.stroke();
      ctx.globalAlpha = Math.min(0.28, level * 0.5);
      ctx.strokeStyle = this.rgb(this.cur[1]);
      ctx.lineWidth = 0.9;
      this.path(cx, cy, base * (1.42 + level * 0.5), LAYERS[2], level * 0.6);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    this.frameId = requestAnimationFrame(this.frame);
  }
}
